import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role } from '@gobingebox/shared';
import { Loader2 } from '@/components/icons';
import { NotFoundPage } from '@/pages/NotFound';
import { useAuth } from './AuthContext';

function FullPageLoader() {
  return (
    <div className="grid min-h-[60vh] place-items-center">
      <Loader2 className="animate-spin text-brand-soft" size={28} />
    </div>
  );
}

/** Require a signed-in user; otherwise bounce to /login with a return path. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <FullPageLoader />;
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Require one of the given roles. Logged-out users go to /login; a logged-in
 * user without the role gets a 404 (the page acts as if it doesn't exist, so
 * the Dev Panel's existence isn't revealed).
 */
export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <FullPageLoader />;
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (!roles.includes(user.role)) return <NotFoundPage />;
  return <>{children}</>;
}

/** Pages only for signed-out users (login/signup) → send authed users to browse. */
export function GuestOnly({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullPageLoader />;
  if (user) return <Navigate to="/browse" replace />;
  return <>{children}</>;
}
